import 'core-js/modules/es.array.iterator.js';
import 'core-js/modules/web.dom-collections.iterator.js';
import 'core-js/modules/es.object.assign.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { Stack, Text } from '@hub-la/design-system';
import { Avatar } from '@mui/material';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommentMention } from '../../../usecases/comment-mention.js';
import img from '../../assets/default-user-avatar.js';
import { usePutComment } from '../../hooks/use-put-comment.js';
import { usePutLikeComment } from '../../hooks/use-put-like-comment.js';
import { useDiscussion } from '../../providers/discussion.js';
import { InputComment } from '../input-comment/input-comment.js';
import { CommentActions } from './actions.js';
import { CommentOptions } from './options.js';
import { CommentReplies } from './replies.js';

const Comment = ({
  commentId,
  fullName,
  avatarUrl,
  avatarSize: _avatarSize = 40,
  comment,
  isEdited,
  isReply,
  createdAt,
  isLiked,
  likes,
  repliesCount: _repliesCount = 0,
  latestReplyAt,
  parentCommentId,
  userId,
  hasOwnerReply,
  discussionOwner
}) => {
  const [like, setLike] = useState(isLiked);
  const [isEditing, setIsEditing] = useState(false);
  const [toggleReplies, setToggleReplies] = useState(false);
  const {
    t
  } = useTranslation();
  const {
    isDiscussionOwner,
    isOwnerOfComment,
    currentUser
  } = useDiscussion();
  const {
    mutateAsync: editComment,
    isLoading: editCommentLoading
  } = usePutComment({
    onSuccess: () => {
      setIsEditing(false);
    },
    isReply,
    parentCommentId
  });
  const {
    mutateAsync: likeComment
  } = usePutLikeComment();
  const handleEdit = () => {
    setIsEditing(true);
  };
  const handleLike = () => {
    likeComment({
      commentId
    });
    setLike(!like);
    /**@todo: update like comments data in cache */
  };
  // Show replies component only if there are any and the comment is not a reply
  const showReplies = _repliesCount > 0 && !isReply;
  const showCommentOptions = !isEditing && (isDiscussionOwner || isOwnerOfComment(userId));
  const commentButtonLabel = isEditing ? t('editInputComment.submit') : t('inputComment.submit');
  const createdFormated = moment(createdAt).locale('pt-br').fromNow();
  const {
    mention,
    message
  } = new CommentMention().execute(comment);
  return jsxs(Stack, Object.assign({
    justifyContent: "space-between",
    direction: "row",
    gap: 3,
    width: "100%"
  }, {
    children: [jsxs(Stack, Object.assign({
      direction: "row",
      gap: 3,
      width: "100%"
    }, {
      children: [jsx(Avatar, {
        src: avatarUrl !== null && avatarUrl !== void 0 ? avatarUrl : String(img),
        sx: {
          width: _avatarSize,
          height: _avatarSize
        },
        "data-testid": "avatar"
      }), jsxs(Stack, Object.assign({
        width: "100%"
      }, {
        children: [isEditing && jsx(InputComment, {
          defaultComment: comment,
          onCancel: () => setIsEditing(false),
          isLoading: editCommentLoading,
          onComment: message => {
            editComment({
              commentId,
              message
            });
          },
          avatarUrl: currentUser === null || currentUser === void 0 ? void 0 : currentUser.picture,
          commentButtonLabel: commentButtonLabel,
          showAvatar: false,
          inputLabel: isReply ? t('editInputComment.replyLabel') : t('editInputComment.label')
        }), !isEditing && jsxs(Fragment, {
          children: [jsx(Stack, {
            children: jsxs(Stack, Object.assign({
              direction: "row",
              alignItems: "baseline",
              gap: 2,
              mb: 1
            }, {
              children: [jsx(Text, Object.assign({
                variant: "body2"
              }, {
                children: fullName
              })), jsxs(Text, Object.assign({
                variant: "caption",
                color: "neutralPrimary"
              }, {
                children: [createdFormated, " ", isEdited && t('comment.editedFlag')]
              }))]
            }))
          }), jsxs(Text, Object.assign({
            mb: 2,
            whiteSpace: "pre-wrap",
            style: {
              wordWrap: 'break-word'
            }
          }, {
            children: [mention && jsx(Text, Object.assign({
              color: "primary",
              display: "inline"
            }, {
              children: mention
            })), message]
          }))]
        }), jsx(CommentActions, {
          onLike: handleLike,
          isLiked: like,
          likes: likes,
          isReply: isReply,
          parentCommentId: isReply ? parentCommentId : commentId,
          onReplySuccess: () => setToggleReplies(true),
          parentUserId: userId,
          parentUserName: fullName
        }), showReplies && jsx(CommentReplies, {
          commentId: commentId,
          latestReplyAt: latestReplyAt,
          count: _repliesCount,
          hasOwnerReply: hasOwnerReply,
          discussionOwner: discussionOwner,
          setToggleReplies: setToggleReplies,
          toggleReplies: toggleReplies
        })]
      }))]
    })), showCommentOptions && jsx(CommentOptions, {
      commentId: commentId,
      userId: userId,
      isReply: isReply,
      parentCommentId: parentCommentId,
      onEdit: handleEdit
    })]
  }));
};

export { Comment };
